import Box from '@mui/material/Box'
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro'
import React, { useCallback, useMemo, useState } from 'react'

import ActivityCell from 'components/common/activity/ActivityCell'
import ActivityHistoryActions from 'components/common/activity/ActivityHistoryActions'
import HHAccordion from 'components/common/HHAccordion'
import useFilterHandlers from 'components/filter/useFilterHandlers'
import usePersistedFilterModel from 'components/filter/usePersistedFilterModel'
import T from 'T'

import AccountActivityProvider, { useAccountActivity } from './AccountActivityProvider'

import type { GridApiPro, GridColDef, GridFilterModel } from '@mui/x-data-grid-pro'
import type { ActivityRow } from 'utils/activity/ActivityTreeBuilder'

// AccountActivityProvider is untyped .jsx — drop these casts when it migrates.
type AccountActivity = { treeData: ActivityRow[]; isLoading: boolean }
type PersistedFilterModel = [GridFilterModel, (model: GridFilterModel) => void]

const COLUMN_VISIBILITY = {
  field: false,
  oldValue: false,
  newValue: false,
  modifiedBy: false,
  modifiedAt: false,
  changeType: false,
  rev: false,
}

type AccountActivityGridProps = {
  apiRef: React.MutableRefObject<GridApiPro>
  filterAnchorEl: HTMLElement | null
}

const AccountActivityGrid = ({ apiRef, filterAnchorEl }: AccountActivityGridProps) => {
  const [filterModel, setFilterModel] = usePersistedFilterModel('ACCOUNT_ACTIVITY_ACCORDION_FILTER_MODEL') as PersistedFilterModel
  const { handleFilterModelChange } = useFilterHandlers(filterModel, setFilterModel, true)
  // Every value column is hidden here, and MUI drops hidden columns from the quick filter by default.
  const quickFilterableModel = useMemo(() => ({ ...filterModel, quickFilterExcludeHiddenColumns: false }), [filterModel])

  const getRowHeight = useCallback(() => 'auto' as const, [])

  const { treeData, isLoading } = useAccountActivity() as AccountActivity

  const columns = useMemo<GridColDef<ActivityRow>[]>(
    () => [
      { field: 'id', disableExport: true, flex: 1, renderCell: ActivityCell },
      { field: 'field', type: 'string', filterable: true },
      { field: 'modifiedBy', type: 'string', filterable: true },
      { field: 'modifiedAt', type: 'string', filterable: true },
      { field: 'oldValue', type: 'string', filterable: true },
      { field: 'newValue', type: 'string', filterable: true },
      { field: 'changeType', type: 'string' },
    ],
    []
  )

  return (
    <Box display="block">
      <DataGridPro
        disableColumnMenu
        disableColumnResize
        disableColumnSelector
        disableDensitySelector
        apiRef={apiRef}
        columnHeaderHeight={0}
        columns={columns}
        columnVisibilityModel={COLUMN_VISIBILITY}
        filterModel={quickFilterableModel}
        getRowHeight={getRowHeight}
        loading={isLoading}
        rows={treeData}
        slotProps={{ panel: { anchorEl: filterAnchorEl } }}
        sx={{ maxHeight: 'calc(100vh - 400px)' }}
        onFilterModelChange={handleFilterModelChange}
      />
    </Box>
  )
}

type AccountActivityAccordionProps = {
  accountId?: string
  billingAddressId?: string
}

// apiRef is created here, not in the grid, so the header actions can drive it.
const AccountActivityAccordion = ({ accountId = '', billingAddressId = '' }: AccountActivityAccordionProps) => {
  const apiRef = useGridApiRef()
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null)

  return (
    <HHAccordion
      isExpandCollapseHeightSame
      isExpanded
      showCustomActions
      withNewColors
      customActionContent={<ActivityHistoryActions apiRef={apiRef} onFilterAnchorChange={setFilterAnchorEl} />}
      showActionIcon={false}
      summary={T.ACTIVITY_HISTORY}
    >
      <AccountActivityProvider accountId={accountId} billingAddressId={billingAddressId}>
        <AccountActivityGrid apiRef={apiRef} filterAnchorEl={filterAnchorEl} />
      </AccountActivityProvider>
    </HHAccordion>
  )
}

export default AccountActivityAccordion
